import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX, POPUP_COLORS } from '../../constants/popupStyles';
import { QuestionnairePopup, PopupButton } from '../../types/questionnaire.types';

interface PopupEditorProps {
  open: boolean;
  popup?: QuestionnairePopup;
  onSave: (popup: QuestionnairePopup) => void;
  onCancel: () => void;
}

interface SortableButtonItemProps {
  button: PopupButton;
  onEdit: () => void;
  onDelete: () => void;
}

const inputLabelProps = { shrink: true, sx: { color: POPUP_COLORS.titleText } };
const inputProps = { style: CLINIC_DIALOG_SX.textField as React.CSSProperties };

const SortableButtonItem: React.FC<SortableButtonItemProps> = ({ button, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: button.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'close-popup': return 'Close Popup';
      case 'next': return 'Next Section';
      default: return action;
    }
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1.5,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: POPUP_COLORS.fieldBackground,
        borderRadius: '8px',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      <IconButton
        {...attributes}
        {...listeners}
        size="small"
        sx={{ cursor: isDragging ? 'grabbing' : 'grab', color: POPUP_COLORS.titleText }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight="600" sx={{ color: POPUP_COLORS.titleText }}>
          {button.label}
        </Typography>
        <Typography variant="caption" sx={{ color: POPUP_COLORS.titleText, opacity: 0.7 }}>
          {getActionLabel(button.action)} • {button.buttonStyle || 'primary'} • {button.buttonHeight || 'medium'}
        </Typography>
      </Box>
      <Button size="small" onClick={onEdit} sx={CLINIC_DIALOG_SX.cancelButton}>Edit</Button>
      <IconButton size="small" onClick={onDelete} sx={{ color: '#c62828' }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const PopupEditor: React.FC<PopupEditorProps> = ({ open, popup, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [buttons, setButtons] = useState<PopupButton[]>([]);
  const [buttonLayout, setButtonLayout] = useState<'center' | 'stack' | 'space-between'>('center');

  const [buttonFormOpen, setButtonFormOpen] = useState(false);
  const [editingButtonIndex, setEditingButtonIndex] = useState<number | null>(null);
  const [buttonLabel, setButtonLabel] = useState('');
  const [buttonAction, setButtonAction] = useState<'close-popup' | 'next' | 'submit' | 'exit' | 'exit-screening'>('close-popup');
  const [buttonStyle, setButtonStyle] = useState<'primary' | 'secondary'>('primary');
  const [buttonHeight, setButtonHeight] = useState<'small' | 'medium' | 'large'>('medium');
  const [buttonExitUrl, setButtonExitUrl] = useState('');

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link', 'color', 'background',
  ];

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (open) {
      if (popup) {
        setTitle(popup.title);
        setContent(popup.content);
        setButtons(popup.buttons || []);
        setButtonLayout(popup.buttonLayout || 'center');
      } else {
        setTitle('');
        setContent('');
        setButtons([]);
        setButtonLayout('center');
      }
      setButtonFormOpen(false);
      setEditingButtonIndex(null);
    }
  }, [open, popup]);

  const handleOpenButtonForm = () => {
    setButtonLabel('');
    setButtonAction('close-popup');
    setButtonStyle('primary');
    setButtonHeight('medium');
    setButtonExitUrl('');
    setEditingButtonIndex(null);
    setButtonFormOpen(true);
  };

  const handleEditButton = (index: number) => {
    const button = buttons[index];
    setButtonLabel(button.label);
    setButtonAction(button.action);
    setButtonStyle(button.buttonStyle || 'primary');
    setButtonHeight(button.buttonHeight || 'medium');
    setButtonExitUrl(button.exitUrl || '');
    setEditingButtonIndex(index);
    setButtonFormOpen(true);
  };

  const handleSaveButton = () => {
    if (!buttonLabel.trim()) return;

    const newButton: PopupButton = {
      id: editingButtonIndex !== null ? buttons[editingButtonIndex].id : `btn_${Date.now()}`,
      label: buttonLabel.trim(),
      action: buttonAction,
      buttonStyle,
      buttonHeight,
      ...(buttonAction === 'exit' && buttonExitUrl.trim() ? { exitUrl: buttonExitUrl.trim() } : {}),
      order: editingButtonIndex !== null ? buttons[editingButtonIndex].order : buttons.length,
    };

    if (editingButtonIndex !== null) {
      const updated = [...buttons];
      updated[editingButtonIndex] = newButton;
      setButtons(updated);
    } else {
      setButtons([...buttons, newButton]);
    }
    setButtonFormOpen(false);
    setEditingButtonIndex(null);
  };

  const handleDeleteButton = (index: number) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = buttons.findIndex((b) => b.id === active.id);
      const newIndex = buttons.findIndex((b) => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        setButtons(arrayMove(buttons, oldIndex, newIndex));
      }
    }
  };

  const handleSave = () => {
    if (!title.trim() || buttons.length === 0) return;
    onSave({
      id: popup?.id || `popup_${Date.now()}`,
      title: title.trim(),
      content: content.trim(),
      buttons,
      buttonLayout,
    });
  };

  const mainActions = (
    <>
      <Button onClick={onCancel} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={!title.trim() || buttons.length === 0}
        sx={CLINIC_DIALOG_SX.primaryButton}
      >
        Save Popup
      </Button>
    </>
  );

  const buttonFormActions = (
    <>
      <Button onClick={() => setButtonFormOpen(false)} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
      <Button
        variant="contained"
        onClick={handleSaveButton}
        disabled={!buttonLabel.trim()}
        sx={CLINIC_DIALOG_SX.primaryButton}
      >
        {editingButtonIndex !== null ? 'Update' : 'Add'}
      </Button>
    </>
  );

  return (
    <>
      <ClinicDialog
        open={open && !buttonFormOpen}
        onClose={onCancel}
        title={popup ? 'Edit Popup' : 'Create Popup'}
        maxWidth="md"
        actions={mainActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Title */}
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            required
            label="Popup Title (For Dropdown Identification)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Exit Confirmation"
            helperText="This title is used to identify the popup in the button dropdown. It won't be displayed in the popup itself."
            variant="outlined"
            InputLabelProps={inputLabelProps}
            InputProps={inputProps}
          />

          {/* HTML Content */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: POPUP_COLORS.titleText }}>
              Content
            </Typography>
            <ReactQuill
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              style={{ height: '180px', marginBottom: '50px' }}
            />
          </Box>

          {/* Button Layout */}
          <FormControl fullWidth variant="outlined">
            <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Button Layout</InputLabel>
            <Select
              value={buttonLayout}
              label="Button Layout"
              onChange={(e) => setButtonLayout(e.target.value as any)}
              sx={CLINIC_DIALOG_SX.select}
            >
              <MenuItem value="center">Center (Horizontal)</MenuItem>
              <MenuItem value="stack">Stack (Vertical)</MenuItem>
              <MenuItem value="space-between">Space Between (Left & Right)</MenuItem>
            </Select>
          </FormControl>

          {/* Buttons Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: POPUP_COLORS.titleText }}>Buttons</Typography>
              <Button size="small" variant="outlined" onClick={handleOpenButtonForm} sx={CLINIC_DIALOG_SX.cancelButton}>
                Add Button
              </Button>
            </Box>

            {buttons.length === 0 ? (
              <Typography variant="body2" sx={{ textAlign: 'center', py: 2, color: POPUP_COLORS.titleText, opacity: 0.7 }}>
                No buttons yet. Add at least one button.
              </Typography>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={buttons.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                  <Box>
                    {buttons.map((button, index) => (
                      <SortableButtonItem
                        key={button.id}
                        button={button}
                        onEdit={() => handleEditButton(index)}
                        onDelete={() => handleDeleteButton(index)}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            )}
          </Box>
        </Box>
      </ClinicDialog>

      {/* Button Form Dialog */}
      <ClinicDialog
        open={buttonFormOpen}
        onClose={() => setButtonFormOpen(false)}
        title={editingButtonIndex !== null ? 'Edit Button' : 'Add Button'}
        maxWidth="sm"
        actions={buttonFormActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            sx={{ mt: 1 }}
            fullWidth
            label="Button Label"
            value={buttonLabel}
            onChange={(e) => setButtonLabel(e.target.value)}
            required
            placeholder="e.g., Continue Screening"
            variant="outlined"
            InputLabelProps={inputLabelProps}
            InputProps={inputProps}
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Action</InputLabel>
            <Select
              value={buttonAction}
              label="Action"
              onChange={(e) => setButtonAction(e.target.value as any)}
              sx={CLINIC_DIALOG_SX.select}
            >
              <MenuItem value="close-popup">Close Popup</MenuItem>
              <MenuItem value="next">Next Section</MenuItem>
              <MenuItem value="exit-screening">Exit Screening</MenuItem>
            </Select>
          </FormControl>

          {buttonAction === 'exit' && (
            <TextField
              fullWidth
              label="Exit URL"
              value={buttonExitUrl}
              onChange={(e) => setButtonExitUrl(e.target.value)}
              placeholder="https://example.com"
              variant="outlined"
              InputLabelProps={inputLabelProps}
              InputProps={inputProps}
            />
          )}

          <FormControl fullWidth variant="outlined">
            <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Button Style</InputLabel>
            <Select
              value={buttonStyle}
              label="Button Style"
              onChange={(e) => setButtonStyle(e.target.value as any)}
              sx={CLINIC_DIALOG_SX.select}
            >
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel shrink sx={{ color: POPUP_COLORS.titleText }}>Button Height</InputLabel>
            <Select
              value={buttonHeight}
              label="Button Height"
              onChange={(e) => setButtonHeight(e.target.value as any)}
              sx={CLINIC_DIALOG_SX.select}
            >
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </ClinicDialog>
    </>
  );
};
